import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {
  AlertService,
  CampaignTrackingService,
  DeploymentLinkService,
  FunnelStepService,
  UserService,
} from '../_services';
import {FormBuilder, Validators} from '@angular/forms';
import {
  Campaign,
  CustomDomain,
  FunnelInputTypeEnum,
  FunnelStep,
  LinkCustomerMatchMethod,
  LinkCustomerMatchMethodLabels,
  LinkOfferIntents,
  OfferIntentLabels,
  OfferIntents,
  SessionTypeLabels,
  ShopifyPublicPlugin,
  TrackingSource,
  sessionTypeRequiresIntent,
  SessionType,
  FunnelInputTypeLabels,
  SessionStartStepTypes,
  SessionStartHybridStepTypes,
  SessionStartCustomerPortalStepTypes,
  User,
  buildDeploymentLinkUrl,
} from '../_models';
import {NgxSmartModalService} from "ngx-smart-modal";
import {takeUntil} from "rxjs/operators";
import {CrudSavePopupComponent} from '../_directives';
import {copyToClipboard} from '../_helpers';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './campaign-links.component.html',
  selector: 'campaign-links',
  styleUrls: ['./campaign-edit.component.css']
})
export class CampaignLinksComponent extends CrudSavePopupComponent implements OnInit, OnChanges {
  trackingSources: TrackingSource[] = [];
  steps: FunnelStep[] = [];
  selectedSource: TrackingSource;
  generatedUrl = '';
  selectedDomain: CustomDomain = null;
  private selectedStep: FunnelStep = null;
  private actionStep: FunnelStep = null;
  hideAdditionalParams: boolean = false;
  user: User;
  @Input('campaign') campaign: Campaign;
  @Input('domain') domain: CustomDomain;
  @Input('shopifyPlugin') shopifyPlugin: ShopifyPublicPlugin;
  offerIntents: OfferIntents[] = LinkOfferIntents;
  copyValue = copyToClipboard;
  languages = [
    {code: 'en', label: 'English'},
    {code: 'es', label: 'Spanish'},
    {code: 'fr', label: 'French'},
    {code: 'de', label: 'German'},
    {code: 'pt', label: 'Portuguese'},
    {code: 'ru', label: 'Russian'},
    {code: 'zh-CN', label: 'Chinese (Simplified)'},
    {code: 'ko', label: 'Korean'},
    {code: 'ja', label: 'Japanese'},
    {code: 'hi', label: 'Hindi'},
  ];

  matchMethods = [
    {id: LinkCustomerMatchMethod.CustomerId, label: LinkCustomerMatchMethodLabels[LinkCustomerMatchMethod.CustomerId]},
    {id: LinkCustomerMatchMethod.Email, label: LinkCustomerMatchMethodLabels[LinkCustomerMatchMethod.Email]},
    {id: LinkCustomerMatchMethod.Phone, label: LinkCustomerMatchMethodLabels[LinkCustomerMatchMethod.Phone]},
  ];

  public matchMethodFilterSettings = {
    singleSelection: false,
    allowSearchFilter: false,
    enableCheckAll: false,
    textField: 'label',
  };

  sessionTypes = [];
  stepTypes = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    private formBuilder: FormBuilder,
    private trackingService: CampaignTrackingService,
    private stepService: FunnelStepService,
    private modalService: NgxSmartModalService,
    private userService: UserService,
    protected deploymentLinkService: DeploymentLinkService
  ) {
    super(router, location, route, deploymentLinkService, alertService);
    this.languages.sort((a, b) => a.label.localeCompare(b.label));
    this.objectName = 'Deployment Link';
    this.offerIntents.sort((a, b) => OfferIntentLabels[a].localeCompare(OfferIntentLabels[b]));

    for (let i = 1; i < SessionTypeLabels.length; i++) {
      this.sessionTypes.push({value: i, label: SessionTypeLabels[i]});
    }

    this.sessionTypes.sort((a, b) => a.label.localeCompare(b.label));
  }

  ngOnInit() {
    this.setForm(this.formBuilder.group({
      id: [null],
      sid1: [null],
      sid2: [null],
      language: [null],
      tracking_source: [null],
      include_ticket_id: [false],
      name: [this.shopifyPlugin ? 'Shopify Public Plugin' : null, Validators.required],
      step_key: [null],
      step_type: [null],
      session_type: [null],
      is_first_step: [false],
      domain: [this.domain && this.domain.id],
      campaign: [this.campaign && this.campaign.id, Validators.required],
      is_action: [false],
      match_method: [null],
      offer_intent: [null],
    }));

    this.userService.getCurrent().pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: User) => {
          this.user = user;
        },
        error => {
          this.handleError(error);
        }
      );

    this.trackingService.list({campaign: this.campaign.id})
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        sources => {
          this.trackingSources = sources;
        },
        error => {
          this.handleError(error);
        }
      );

    super.ngOnInit();

    this.form.valueChanges.subscribe(() => this.generateUrl())
  }

  ngOnChanges() {
    if (this.form) {
      this.updateForm();
    }
  }

  getDomainType(domain: CustomDomain) {
    return SessionTypeLabels[domain.type];
  }

  getOfferIntentLabel(intent: OfferIntents) {
    const labels = {
      [OfferIntents.CancelOrder]: 'Cancel Order',
      [OfferIntents.CancelSubscription]: 'Cancel Subscription',
      [OfferIntents.ReturnOrder]: 'Return Order',
    }
    return (intent in labels) ? labels[intent] : OfferIntentLabels[intent];
  }

  get isHybrid() {
    return this.campaign && this.campaign.is_hybrid;
  }

  private get isCustomerPortal() {
    return this.getLinkType() === SessionType.CustomerPortal;
  }

  private getLinkType() {
    const sessionType = this.getControlValue('session_type');

    if (sessionType) {
      return sessionType;
    }

    return this.selectedDomain ? this.selectedDomain.type : this.campaign.subdomain_type;
  }

  private get isOfferIntentRequired() {
    return this.isAction && sessionTypeRequiresIntent(this.getLinkType());
  }

  private get activeDomain() {
    return this.selectedDomain ? this.selectedDomain.domain : this.campaign.active_domain;
  }

  private updateStepTypesList() {
    let stepTypes = SessionStartStepTypes;
    let stepTypeOptions = [];
    let allowedStepTypes = {};

    this.steps.forEach(step => {
      allowedStepTypes[step.type] = true;
    });

    if (this.isHybrid) {
      stepTypes = SessionStartHybridStepTypes;
    } else if (this.isCustomerPortal) {
      stepTypes = SessionStartCustomerPortalStepTypes;
    }

    stepTypes.forEach(stepType => {
      if (stepType in allowedStepTypes) {
        stepTypeOptions.push({value: stepType, label: FunnelInputTypeLabels[stepType]});
      }
    });

    stepTypeOptions.sort((a, b) => a.label.localeCompare(b.label));
    this.stepTypes = stepTypeOptions;
  }

  private updateOfferIntentValidation() {
    const offerIntentControl = this.form.get('offer_intent');
    const customerMatchControl = this.form.get('match_method');

    if (this.isOfferIntentRequired) {
      offerIntentControl.setValidators(Validators.required);
    } else {
      offerIntentControl.clearValidators();
    }
    offerIntentControl.updateValueAndValidity();

    if (this.isAction) {
      customerMatchControl.setValidators(Validators.required);

      if (customerMatchControl.value === null) {
        this.form.patchValue({match_method: LinkCustomerMatchMethod.CustomerId});
      }
    } else {
      customerMatchControl.clearValidators();
    }
    customerMatchControl.updateValueAndValidity();
  }

  addNewSubId() {
    this.form.addControl('sid3', this.formBuilder.control(null));
  }

  protected onInputDataChanged() {
    let trackingSourceId = null;
    let name = this.shopifyPlugin ? 'Shopify Public Plugin' : null;
    const matchMethods = [];

    if (this.campaign) {
      if (this.inputData) {
        name = this.inputData.name;
        trackingSourceId = this.inputData.tracking_source &&
          (this.inputData.tracking_source['id'] || this.inputData.tracking_source);

        if (this.inputData.domain) {
          if (this.inputData.domain['id']) {
            this.selectedDomain = this.inputData.domain;
          } else {
            this.selectedDomain = this.campaign.custom_domains.find((domain: CustomDomain) => {
              return domain.id.toString() === this.inputData.domain.toString();
            }) || null;
          }
        }
      }

      if (this.domain) {
        this.selectedDomain = this.domain;
      }

      this.form.patchValue({
        name: name,
        domain: this.selectedDomain ? this.selectedDomain.id : null,
        tracking_source: trackingSourceId,
      });

      this.updateFunnelSteps();
    }
  }

  private updateFunnelSteps() {
    const params: any = {
      resourcetype: 'VisualFunnel',
      funnel__session_type: this.getLinkType(),
    };

    if (sessionTypeRequiresIntent(this.getLinkType())) {
      params.campaigns_or_public = this.campaign.id;
    } else {
      params.funnel__campaigns = this.campaign.id;
    }

    this.stepService.getGlobalJumpSteps(false, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (steps: FunnelStep[]) => {
          const selectedStepKey = this.getControlValue('step_key');
          const selectedStepType = this.getControlValue('step_type');
          this.steps = steps;
          this.actionStep = steps.find((step: FunnelStep) => this.isActionStep(step));
          this.selectedStep = steps.find((step: FunnelStep) =>
            (selectedStepType && step.type === selectedStepType) || (selectedStepKey && step.slug === selectedStepKey));

          if (!this.selectedStep) {
            // clear the selected step if the selected key is no longer a valid step
            this.form.patchValue({step_key: null, step_type: null, is_action: false});
          }

          this.updateStepTypesList();
          this.updateOfferIntentValidation();
          this.generateUrl();
        },
        error => {
          this.handleError(error);
          this.generateUrl();
        }
      );
  }

  get hasSubId3() {
    return this.form && this.form.get('sid3')
  }

  onChangeStep(event) {
    const index = event.currentTarget.selectedIndex;
    this.selectedStep = (index > 0) ? this.steps[index - 1] : null;
    this.form.patchValue({step_type: null});

    if (this.isAction) {
      this.form.patchValue({is_action: true});
    }

    this.updateOfferIntentValidation();
  }

  onChangeStepType(event) {
    this.selectedStep = this.steps.find((step: FunnelStep) => step.type === +event.target.value);
    this.form.patchValue({step_key: null});

    if (this.isAction) {
      this.form.patchValue({is_action: true});
    }

    this.updateOfferIntentValidation();
  }

  onChangeDomain(event) {
    const index = event.currentTarget.selectedIndex;
    this.selectedDomain = (index > 0) ? this.campaign.custom_domains[index - 1] : null;
    this.updateFunnelSteps();
  }

  onChangeSessionType(event) {
    this.updateStepTypesList();
  }

  private isActionStep(step: FunnelStep) {
    return step.type === FunnelInputTypeEnum.EnterProductFunnel;
  }

  get isAction() {
    return this.selectedStep && this.isActionStep(this.selectedStep);
  }

  get canToggleAction() {
    return !!this.actionStep;
  }

  toggleIsAction(event) {
    if (this.canToggleAction) {
      const isAction: boolean = event.target.checked;
      this.selectedStep = isAction ? this.actionStep : null;
      this.form.patchValue({
        step_key: null,
        step_type: this.selectedStep ? this.selectedStep.type : null,
        is_action: isAction
      });
      this.updateOfferIntentValidation();
    }
  }

  createTrackingSource() {
    this.selectedSource = {
      id: null,
      campaign: this.campaign.id,
      source: null
    };
    this.openEditTrackingSourceDialog();
  }

  editTrackingSource(sourceId: number | string) {
    this.trackingSources.some((source: TrackingSource) => {
      if (sourceId.toString() === source.id.toString()) {
        this.selectedSource = source;
        this.openEditTrackingSourceDialog();

        return true;
      }
    });
  }

  private openEditTrackingSourceDialog() {
    this.modalService.getModal('editTrackingSourceDialog').open();
  }

  onSaveTrackingSource(savedSource: TrackingSource) {
    let isNew = true;

    this.modalService.getModal('editTrackingSourceDialog').close();

    if (!this.trackingSources.some((source: TrackingSource, index: number) => {
      if (savedSource.id === source.id) {
        //existing source, update our local value
        this.trackingSources[index] = savedSource;
        return true;
      }
    })) {
      // new source, add it to our list and select it
      this.trackingSources.push(savedSource);
      this.form.patchValue({tracking_source: savedSource.id})
    }

    // re-sort the sources list
    this.trackingSources.sort((a, b): number => {
      if (a.source < b.source) {
        return -1;
      }
      if (a.source > b.source) {
        return 1;
      }
      return 0;
    });
  }

  preview() {
    if (this.generatedUrl) {
      this.router.navigate(['/smartlink-preview'], {
        queryParams: Object.assign({}, this.form.value, {preview_domain: this.activeDomain})
      });
    }
  }

  previewIframe() {
    if (this.generatedUrl) {
      this.router.navigate(['/smartlink-preview'], {queryParams: this.form.value});
    }
  }

  generateUrl() {
    this.generatedUrl = buildDeploymentLinkUrl(this.form.value, this.activeDomain, this.campaign.ticket_system);
  }

  protected getFormData() {
    const data = this.form.value;

    if (this.shopifyPlugin) {
      data['shopify_public_plugin'] = this.shopifyPlugin.id;
    }

    return data;
  }
}
